import { Header, Icon, ToolTip } from '@greystar/designsystem';
import {
  getMonthlyFeeGroups,
  getOneTimeStepOneFees,
  useAnalytics,
  useFeesAnalytics,
  useFormattedAddress,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import CalculateCostsHeader from '../../CalculateCostsHeader/CalculateCostsHeader';
import Counter from '../../Controls/Counter/Counter';
import FeeAccordion from '../../FeeAccordion/FeeAccordion';
import styles from './calculateStep.module.scss';
import CalculateStepProps from './CalculateStep.props';
import LeaseDetailsSection from './LeaseDetailsSection/LeaseDetailsSection';

export default function CalculateStep(props: CalculateStepProps) {
  const oneTimeFees = getOneTimeStepOneFees(props.feeState.allFees);
  const monthlyFees = getMonthlyFeeGroups(props.feeState.allFees);
  const { sitecoreContext } = useSitecoreContext();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const propertyId = property.id ?? '';
  const propertyName = property.name;
  const state = property?.location?.state;
  const property_location = useFormattedAddress(property.location);

  const analytics = useAnalytics();
  const { mergeAnalyticsData } = useFeesAnalytics();

  const handleTooltipClick = (label: string) => {
    analytics.trackEvent('tooltip', {
      action_type: 'click',
      state: state,
      property_location,
      property_id: propertyId,
      property_name: propertyName,
      content_group: label ?? '',
      module_name: 'fees section',
      inpage_location: 'middle',
      click_text: label,
      destination_url: '',
    });
  };

  const updateData = (updateType: string) => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_update',
        action_type: 'update',
        updated_item: 'numberOfApplicants',
        click_text: updateType,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_update', data);
      },
    });
  };

  const updateTooltipData = (label = '') => {
    mergeAnalyticsData({
      overrides: {
        event: 'unit_rent_calc_tooltip',
        action_type: 'click',
        tooltip: label,
        click_text: label,
      },
      onComplete: (data) => {
        analytics.trackEvent('unit_rent_calc_tooltip', data);
      },
    });
  };

  return (
    <div className={styles['calculate-step']}>
      <LeaseDetailsSection {...props} />
      <CalculateCostsHeader activeStep={1} />
      <Counter
        {...props}
        label="Number of applicants"
        handlePlus={(updateFeeProps: { changeAmount: number }) => {
          props.setNumberOfApplicants(props.dispatch)(updateFeeProps.changeAmount);
          updateData('increment');
        }}
        handleMinus={(updateFeeProps: { changeAmount: number }) => {
          props.setNumberOfApplicants(props.dispatch)(updateFeeProps.changeAmount);
          updateData('decrement');
        }}
        defaultValue={props.feeState.numberOfApplicants}
        maxThreshold={9}
        minThreshold={1}
        className={styles['applicant-counter']}
        applicantCounter={true}
      />
      <div className={styles['calculate-step-frequency']}>
        <Header component={'h5'} color={'primary'}>
          One-time costs
        </Header>
        <ToolTip
          className={styles['calculate-step-frequency-tooltip']}
          description={
            'Includes one-time charges due at the time of application, move-in, and/or move-out unless specified otherwise in the application or lease agreement. Excludes base rent, all monthly mandatory, usage-based, and variable fees and any user-selected personalized add-ons.'
          }
          elementToHover={<Icon iconName="QuestionMark" size="sm" />}
          toolTipClick={() => {
            updateTooltipData('One-time costs');
            handleTooltipClick('One-time costs');
          }}
        />
      </div>
      <FeeAccordion
        {...props}
        title="One-time costs"
        fees={oneTimeFees}
        defaultToOpen={true}
        showProration={true}
      />
      <div
        className={`${styles['calculate-step-frequency']} ${styles['calculate-step-frequency--monthly']}`}
      >
        <Header component={'h5'} color={'primary'}>
          Monthly fees
        </Header>
        <ToolTip
          description={
            'Includes monthly required, usage-based, variable fees, and user selected personalized add-ons.  '
          }
          elementToHover={<Icon iconName="QuestionMark" size="sm" />}
          toolTipClick={() => {
            updateTooltipData('Monthly fees');
            handleTooltipClick('Monthly fees');
          }}
        />
      </div>
      <FeeAccordion {...props} title="Monthly fees" feeMap={monthlyFees} defaultToOpen={true} />
    </div>
  );
}
